/**
 * SkuTestSection — BLU-460 follow-up (PLAN §5.3 + §6.2-6.5).
 *
 * Per-SKU Test affordance: operator types a SKU, clicks "Test with SKU",
 * the server resolves the variant via `productVariants(query: "sku:'X'")`
 * and runs the configured mapping against THAT specific product. Reports
 * one of four states:
 *
 *   - `present`         — green band: SKU resolved + the value at NS.KEY
 *                           (composed-filename preview lives in the
 *                           card-level SkuPreviewPanel since Plan 08)
 *   - `not_present`     — amber band: product found, no value at NS.KEY
 *   - `variant_not_found` — gray band: SKU not in store
 *   - `multiple_matches`  — amber band with disambiguation list; operator
 *                           picks which product to validate
 *
 * Lives alongside the existing TestButton inside MappingRow. The two Tests
 * stay INDEPENDENT — they hit different rate-limit-bucket entries (well,
 * the same per-org bucket but separate token consumes) and the result band
 * for one doesn't replace the other.
 *
 * Microcopy: curly quotes, no em-dashes, sentence-case button labels
 * (DESIGN.md §17 + flawless-typography skill).
 *
 * Motion ordering carries forward parent PLAN §9.4 v3.1 P0-3:
 *   t=0       button → spinner + disabled
 *   t=resp    server returns; spinner fades 150ms
 *   t=resp+50 result band mounts (animate-in fade + slide-up)
 *   t=resp+200 button re-enables
 *
 * All buttons set `type="button"` explicitly (nested-form audit).
 */

import { useState } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  CheckCircle2,
  Info,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';
import { useStaggeredTestPhase } from './useStaggeredTestPhase';
// BLU-536 / Phase 56.1 Plan 07 — purpose metadata is single-sourced in
// `./purposes`; this file keeps no local purpose union or label ternary.
import { PURPOSE_SHORT_LABEL, type Purpose } from './purposes';
// Plan 08 — the result union + SKU regex are single-sourced in
// `./sku-test-result` (shared with SkuPreviewPanel); the band shell +
// palette live in `./ResultBand`. This section shows the RESOLVED VALUE
// for the one mapping under test; the composed front/back filename story
// belongs to the card-level SkuPreviewPanel (the legacy single-filename
// `derivedFilename` read was retired with it).
import { SKU_INPUT_REGEX, type SkuTestResult } from './sku-test-result';
import { ResultBand, type BandKind } from './ResultBand';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Motion timings now live in `./useStaggeredTestPhase.ts` — the hook owns
// the spinner-out → stagger → band-in → re-enable sequence, ensuring both
// this component and the parent TestButton share one source of truth.

const TOAST_ID = {
  testError: 'sku-test-error',
  rateLimitError: 'sku-test-rate-limit',
} as const;

interface SkuTestSectionProps {
  /** Purpose of the parent mapping row — used in test calls + aria labels. */
  purpose: Purpose;
  /** Current namespace value from the parent row. */
  namespace: string;
  /** Current key value from the parent row. */
  keyValue: string;
  /** Set true when the parent row's NS+key inputs are both valid. */
  mappingValid: boolean;
}

export function SkuTestSection({
  purpose,
  namespace,
  keyValue,
  mappingValid,
}: SkuTestSectionProps) {
  const [sku, setSku] = useState('');
  const [result, setResult] = useState<SkuTestResult | null>(null);
  const { phase, start, reset, showWithMotion } = useStaggeredTestPhase();

  const testForSkuQuery = trpc.shopifyMetafieldMappings.testMappingForSku.useQuery(
    // BLU-498 — pass `purpose` so the backend reads from the right scope.
    // design_id + back_design_id → product metafield; design_variant →
    // the matched variant's metafield. Without this the variant-scope row
    // would always return `not_present` because the handler defaulted to
    // reading product-level designMetafieldValue.
    { sku, namespace, key: keyValue, purpose },
    { enabled: false, retry: false },
  );

  // For "Test this one" buttons inside the multiple_matches band, we need
  // imperative invocation per product GID. The backend exposes the endpoint
  // as a `.query()`, so we use the vanilla tRPC client via `utils.client`
  // rather than `useQuery` (which would require a fixed input at call site).
  const utils = trpc.useUtils();

  const skuValid = SKU_INPUT_REGEX.test(sku);
  const canTest = mappingValid && skuValid && phase !== 'fetching' && phase !== 'cooling';

  const resetResult = () => {
    setResult(null);
    reset();
  };

  const handleTestClick = async () => {
    start();
    const res = await testForSkuQuery.refetch();
    if (res.error) {
      // The server's TOO_MANY_REQUESTS surface comes through as a TRPCError
      // with `code === 'TOO_MANY_REQUESTS'`. Show distinct copy.
      const isRateLimited = (res.error.data?.code as string | undefined) === 'TOO_MANY_REQUESTS';
      if (isRateLimited) {
        toast.error(res.error.message, {
          duration: Infinity,
          id: TOAST_ID.rateLimitError,
        });
      } else {
        toast.error(`Test failed: ${res.error.message}`, { id: TOAST_ID.testError });
      }
      reset();
      return;
    }
    if (!res.data) {
      reset();
      return;
    }
    // Const capture so the !undefined narrowing survives into the closure
    // (property narrowing on `res.data` is reset inside callbacks).
    const data = res.data;
    showWithMotion(() => {
      setResult(data);
    });
  };

  const handleSkuKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // ALWAYS preventDefault on Enter regardless of `canTest` — otherwise
      // a falling-through Enter would bubble up and submit the parent
      // OrganizationSettings.tsx `<form>` (the nested-form footgun the
      // parent PLAN's §9.1 audit was designed to catch).
      e.preventDefault();
      if (canTest) {
        void handleTestClick();
      }
    } else if (e.key === 'Escape') {
      // Only swallow Escape when there's actually something to clear.
      // Otherwise let it bubble so it can close parent modals/popovers.
      if (sku || result) {
        e.preventDefault();
        setSku('');
        resetResult();
      }
    }
  };

  const handleTestThisOne = async (productGid: string) => {
    start();
    try {
      const data = await utils.client.shopifyMetafieldMappings.testMappingForProductGid.query({
        productGid,
        namespace,
        key: keyValue,
        // BLU-498 — same purpose-aware routing as the per-SKU path.
        purpose,
      });
      showWithMotion(() => {
        // The server returns the present/not_present subset; we widen
        // back to the SkuTestResult shape so the band can render it.
        setResult({ ...data, sku: data.sku || sku });
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Test failed: ${message}`, { id: TOAST_ID.testError });
      reset();
    }
  };

  const showSpinner = phase === 'fetching' || phase === 'cooling';

  return (
    <div className="flex w-full flex-col gap-2 pt-1">
      <div className="flex w-full flex-wrap items-center gap-2">
        <Input
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          onKeyDown={handleSkuKeyDown}
          placeholder="Enter a SKU"
          className="h-8 max-w-[260px] font-mono text-xs"
          aria-label={`Test ${PURPOSE_SHORT_LABEL[purpose]} mapping with a specific SKU`}
          aria-invalid={sku !== '' && !skuValid}
          disabled={showSpinner}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleTestClick}
          disabled={!canTest}
        >
          {showSpinner ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              Testing&hellip;
            </>
          ) : (
            'Test with SKU'
          )}
        </Button>
      </div>

      {sku && !skuValid && (
        <p className="text-xs text-destructive">
          SKU must be 1-64 chars: letters, digits, dot, underscore, or hyphen.
        </p>
      )}

      {/* Single mount per test result. The `key` ties remount to the result
          identity — a NEW result causes a fresh element, replaying the
          animation; a phase transition (`showing → idle`) on the SAME
          result keeps the same element so `aria-live` doesn't re-announce
          (the double-mount in the earlier draft would have triggered SR
          re-read on every phase tick). */}
      {result && (
        <SkuTestResultBand
          key={`${result.sku}-${result.status}-${namespace}-${keyValue}`}
          result={result}
          mappingNamespace={namespace}
          mappingKey={keyValue}
          onTestThisOne={handleTestThisOne}
          animateIn={phase === 'showing'}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result band (4 semantic states)
// ---------------------------------------------------------------------------

interface SkuTestResultBandProps {
  result: SkuTestResult;
  mappingNamespace: string;
  mappingKey: string;
  onTestThisOne: (productGid: string) => void;
  animateIn?: boolean;
}

function SkuTestResultBand({
  result,
  mappingNamespace,
  mappingKey,
  onTestThisOne,
  animateIn = true,
}: SkuTestResultBandProps) {
  let kind: BandKind;
  let icon: React.ReactNode;

  if (result.status === 'present') {
    kind = 'green';
    icon = <CheckCircle2 className="h-4 w-4" aria-hidden="true" />;
  } else if (result.status === 'not_present') {
    kind = 'amber';
    icon = <AlertCircle className="h-4 w-4" aria-hidden="true" />;
  } else if (result.status === 'multiple_matches') {
    kind = 'amber';
    icon = <AlertCircle className="h-4 w-4" aria-hidden="true" />;
  } else {
    kind = 'gray';
    icon = <Info className="h-4 w-4" aria-hidden="true" />;
  }

  return (
    <ResultBand kind={kind} animateIn={animateIn}>
      {result.status === 'present' && (
        <>
          <div className="flex items-start gap-2 min-w-0">
            <span className="mt-0.5">{icon}</span>
            <div className="flex-1 min-w-0 space-y-1">
              <p className="font-medium leading-tight">
                <span className="font-mono">{result.sku}</span> to{' '}
                <span className="font-mono">{result.resolvedValue}</span> on{' '}
                {result.productTitle}
              </p>
              {/* Plan 08 — the composed-filename preview moved to the
                  card-level SkuPreviewPanel (full front+back story); this
                  band stays focused on the one mapping under test. */}
            </div>
          </div>
        </>
      )}

      {result.status === 'not_present' && (
        <div className="flex items-start gap-2 min-w-0">
          <span className="mt-0.5">{icon}</span>
          <div className="flex-1 min-w-0 space-y-1">
            <p className="font-medium leading-tight">
              <span className="font-mono">{result.sku}</span> found on{' '}
              {result.productTitle || 'product'}: no value at{' '}
              <span className="font-mono">
                {mappingNamespace}.{mappingKey}
              </span>
              .
            </p>
          </div>
        </div>
      )}

      {result.status === 'variant_not_found' && (
        <div className="flex items-start gap-2 min-w-0">
          <span className="mt-0.5">{icon}</span>
          <div className="flex-1 min-w-0 space-y-1">
            <p className="font-medium leading-tight">
              No variant with SKU <span className="font-mono">{result.sku}</span> found
              in your Shopify store. Check the SKU or add it in Shopify.
            </p>
          </div>
        </div>
      )}

      {result.status === 'multiple_matches' && (
        <>
          <div className="flex items-start gap-2 min-w-0">
            <span className="mt-0.5">{icon}</span>
            <div className="flex-1 min-w-0 space-y-2">
              <p className="font-medium leading-tight">
                Found <span className="tabular-nums">{result.matches.length}</span> variants
                with SKU <span className="font-mono">{result.sku}</span>.
              </p>
              <ul className="space-y-1 text-xs">
                {result.matches.map((m, i) => (
                  <li
                    key={m.productGid + i}
                    className="flex flex-wrap items-center gap-2 rounded border bg-background/60 px-2 py-1"
                  >
                    <span className="min-w-0 flex-1 truncate font-mono">
                      {m.productTitle}
                      {m.variantTitle ? ` / ${m.variantTitle}` : ''}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => onTestThisOne(m.productGid)}
                    >
                      Test this one
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </ResultBand>
  );
}
